import type { FunctionComponent, ReactElement } from 'react';
import { useState } from 'react';
import { Component as DownChevronIcon } from 'icons/icon-ui--chevron-down.svg';
import { Component as UpChevronIcon } from 'icons/icon-ui--chevron-up.svg';

type DropdownOption = {
  value: any,
  label: string,
  icon?: FunctionComponent,
}

type Props = {
  selectedOption: DropdownOption;
  options: DropdownOption[];
  title?: string;
  onSelect: (option: DropdownOption) => void;
};

function DropdownWithIcon({
  selectedOption,
  options,
  title,
  onSelect,
}: Props): ReactElement {
  const [isExpanded, setIsExpanded] = useState(false);

  const SelectedOptionIcon = selectedOption?.icon;

  const handleSelect = (option: DropdownOption) => {
    setIsExpanded(false);
    onSelect(option);
  };

  const collapseIfFocusedOutside = (e) => {
    if (!e.currentTarget.offsetParent.contains(e.relatedTarget)) {
      setIsExpanded(false);
    }
  };

  return (
    <div className="c-dropdown-with-icon">
      {title && (<h4 className="c-heading--brand-8 c-heading--no-margin">{title}</h4>)}
      <div
        className="c-dropdown-with-icon__button"
        role="button"
        tabIndex={0}
        onClick={() => setIsExpanded(!isExpanded)}
        onKeyUp={(e) => e.key === 'Enter' && setIsExpanded(!isExpanded)}
        onBlur={collapseIfFocusedOutside}
      >
        <div className="c-dropdown-with-icon__selected">
          {SelectedOptionIcon && (
            <span className="c-dropdown-with-icon__icon">
              <SelectedOptionIcon />
            </span>
          )}
          <span className="u-text--sm">{selectedOption?.label}</span>
        </div>
        {isExpanded ? <UpChevronIcon /> : <DownChevronIcon />}
      </div>

      {isExpanded && (
        <div className="c-dropdown-with-icon__list">
          {options.map((option) => (
            <div
              className={`c-dropdown-with-icon__option u-text--sm ${selectedOption?.label === option.label ? 'is-selected' : ''}`}
              role="option"
              tabIndex={0}
              key={option.value}
              onClick={() => handleSelect(option)}
              onKeyUp={(e) => e.key === 'Enter' && handleSelect(option)}
              aria-selected={selectedOption?.value === option.value}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default DropdownWithIcon;
